import React, { useState, useRef, useMemo, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, Loader2, FileText, Sparkles, AlertCircle, CheckCircle2, X,
  Mic, Square, AudioLines,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';

const TEXT_EXTS = ['.txt', '.md', '.docx', '.pdf'];
const AUDIO_EXTS = ['.mp3', '.m4a', '.wav', '.webm', '.mp4', '.mpeg', '.mpga'];
const ALLOWED_EXTS = [...TEXT_EXTS, ...AUDIO_EXTS];
const MAX_TEXT_BYTES = 5 * 1024 * 1024;
const MAX_AUDIO_BYTES = 25 * 1024 * 1024;
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * v260.16 — Transcript → Requirements import dialog.
 *
 * Three states:
 *   1. UPLOAD: file picker + drag-and-drop
 *   2. PARSING: spinner while Claude reads the transcript
 *   3. PREVIEW: per-section checklist of what Claude found; user
 *      ticks the sections they want to merge and clicks "Apply"
 *
 * Merge strategy: for ticked sections, the parsed field values fully
 * replace the existing section payload — the per-section toggle is
 * the user's "I trust the AI for this section" signal, so we don't
 * second-guess it inside the section.
 */
export default function TranscriptImportDialog({
  open, onOpenChange, estimateId, catalog, onApply, resumeJobId = null,
}) {
  const [stage, setStage] = useState('upload'); // 'upload' | 'parsing' | 'preview'
  const [dragOver, setDragOver] = useState(false);
  const [parsedResult, setParsedResult] = useState(null);
  const [selectedSections, setSelectedSections] = useState({});
  const [errorMsg, setErrorMsg] = useState('');
  const [asyncNote, setAsyncNote] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  // v260.17 — Voice memo recording state.
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordTimerRef = useRef(null);

  // Stop the recorder + release the mic stream when the dialog closes
  // mid-recording so the browser's "recording" indicator goes away.
  // Also clear any in-flight job polling.
  useEffect(() => () => { stopRecording(true); stopPolling(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sectionsById = useMemo(() => {
    const map = {};
    for (const sec of catalog?.narrative_taxonomy || []) map[sec.id] = sec;
    return map;
  }, [catalog]);

  const reset = () => {
    setStage('upload');
    setParsedResult(null);
    setSelectedSections({});
    setErrorMsg('');
    setAsyncNote(false);
    setCurrentJobId(null);
    stopPolling();
    stopRecording(true);
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  const validateFile = (f) => {
    if (!f) return 'Please choose a file to upload.';
    const fn = (f.name || '').toLowerCase();
    const ext = ALLOWED_EXTS.find((e) => fn.endsWith(e));
    if (!ext) {
      return `Unsupported file. Allowed: ${ALLOWED_EXTS.join(', ')}`;
    }
    const isAudio = AUDIO_EXTS.includes(ext);
    const cap = isAudio ? MAX_AUDIO_BYTES : MAX_TEXT_BYTES;
    if (f.size > cap) {
      return `File exceeds ${Math.round(cap / (1024 * 1024))} MB limit`;
    }
    return null;
  };

  // Resume a background job when the dialog is opened from the
  // "Transcript ready" / "Analyzing…" badge on the Requirements page.
  useEffect(() => {
    if (open && resumeJobId) resumeJob(resumeJobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resumeJobId]);

  const consumeJob = async (jobId) => {
    if (!jobId) return;
    try {
      await axios.post(
        `${API}/api/estimates/${estimateId}/requirements/parse-transcript/jobs/${jobId}/consume`,
        {},
        { withCredentials: true },
      );
    } catch { /* best-effort */ }
  };

  const resumeJob = async (jobId) => {
    setCurrentJobId(jobId);
    setErrorMsg('');
    setAsyncNote(true);
    setStage('parsing');
    try {
      const res = await axios.get(
        `${API}/api/estimates/${estimateId}/requirements/parse-transcript/jobs/${jobId}`,
        { withCredentials: true },
      );
      const data = res.data || {};
      if (data.status === 'done') {
        applyParsed(data.result || {});
      } else if (data.status === 'error') {
        setErrorMsg(data.error || 'Transcript analysis failed');
        setStage('upload');
      } else {
        pollJob(jobId);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Could not load the analysis';
      setErrorMsg(typeof detail === 'string' ? detail : 'Could not load the analysis');
      setStage('upload');
    }
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  };

  // Move parsed result into the preview stage (shared by the sync path
  // and the async job-complete path).
  const applyParsed = (data) => {
    setParsedResult(data);
    const initSel = {};
    for (const secId of data?.sections_with_data || []) initSel[secId] = true;
    setSelectedSections(initSel);
    setStage('preview');
  };

  // Poll a background parse job until it's done or errors.
  const pollJob = (jobId) => {
    const tick = async () => {
      try {
        const res = await axios.get(
          `${API}/api/estimates/${estimateId}/requirements/parse-transcript/jobs/${jobId}`,
          { withCredentials: true },
        );
        const data = res.data || {};
        if (data.status === 'done') {
          stopPolling();
          applyParsed(data.result || {});
          toast.success('Transcript analysis complete');
        } else if (data.status === 'error') {
          stopPolling();
          setErrorMsg(data.error || 'Transcript analysis failed');
          setStage('upload');
        } else {
          pollRef.current = setTimeout(tick, 3000);
        }
      } catch (e) {
        stopPolling();
        const detail = e?.response?.data?.detail || e?.message || 'Could not check analysis status';
        setErrorMsg(typeof detail === 'string' ? detail : 'Could not check analysis status');
        setStage('upload');
      }
    };
    pollRef.current = setTimeout(tick, 3000);
  };

  const sendToBackend = async (f) => {
    const err = validateFile(f);
    if (err) {
      setErrorMsg(err);
      return;
    }
    setErrorMsg('');
    setAsyncNote(false);
    setStage('parsing');
    try {
      const form = new FormData();
      form.append('file', f);
      const res = await axios.post(
        `${API}/api/estimates/${estimateId}/requirements/parse-transcript`,
        form,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          // Sync path waits for Claude on small docs; large docs/audio
          // return a job id almost instantly. 3-min ceiling either way.
          timeout: 180_000,
        },
      );
      const data = res.data || {};
      // Large transcript / audio → background job; poll until ready.
      if (data.mode === 'async' && data.job_id) {
        setCurrentJobId(data.job_id);
        setAsyncNote(true);
        pollJob(data.job_id);
        return;
      }
      // Small transcript → synchronous result.
      setCurrentJobId(null);
      applyParsed(data);
    } catch (e) {
      console.error('Transcript parse failed', e);
      const detail = e?.response?.data?.detail || e?.message || 'Parse failed';
      setErrorMsg(typeof detail === 'string' ? detail : 'Parse failed');
      setStage('upload');
    }
  };

  // v260.17 — Browser-native voice memo recording via MediaRecorder.
  // Records to whatever codec the browser supports best (webm/opus on
  // Chrome/Edge/Firefox, m4a on Safari) and pipes the Blob into the
  // same `sendToBackend` pipeline as a regular file upload.
  const startRecording = async () => {
    if (recording) return;
    setErrorMsg('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setErrorMsg('Voice recording is not supported in this browser. Please upload an audio file instead.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Prefer webm/opus (broadly compatible with Whisper). Fall back
      // to whatever default the platform offers (Safari→m4a).
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '');
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        // Release the mic immediately so the browser's "recording" UI clears.
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
        const recordedMime = recorder.mimeType || 'audio/webm';
        const ext = recordedMime.includes('mp4') || recordedMime.includes('m4a')
          ? '.m4a'
          : (recordedMime.includes('webm') ? '.webm' : '.webm');
        const blob = new Blob(chunksRef.current, { type: recordedMime });
        const file = new File([blob], `voice-memo${ext}`, { type: recordedMime });
        sendToBackend(file);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setRecordSeconds(0);
      recordTimerRef.current = setInterval(
        () => setRecordSeconds((s) => s + 1),
        1000,
      );
    } catch (err) {
      console.error('getUserMedia failed', err);
      if (err.name === 'NotAllowedError') {
        setErrorMsg('Microphone access denied. Please allow microphone access in your browser settings and try again.');
      } else {
        setErrorMsg(`Could not start recording: ${err.message || err.name}`);
      }
    }
  };

  function stopRecording(skipUpload = false) {
    // Clear interval timer
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    const rec = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    setRecording(false);
    setRecordSeconds(0);
    if (rec && rec.state !== 'inactive') {
      if (skipUpload) {
        // Replace onstop with a no-op so we don't upload partial audio
        rec.onstop = () => {
          try {
            rec.stream?.getTracks?.().forEach((t) => t.stop());
          } catch { /* noop */ }
        };
      }
      try { rec.stop(); } catch { /* noop */ }
    }
  }

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (f) sendToBackend(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) sendToBackend(f);
  };

  const handleApply = () => {
    if (!parsedResult) return;
    const picked = {};
    for (const [secId, on] of Object.entries(selectedSections)) {
      if (on && parsedResult.parsed[secId]) picked[secId] = parsedResult.parsed[secId];
    }
    if (!Object.keys(picked).length) {
      toast.error('Select at least one section to apply.');
      return;
    }
    // v260.18 — pass the new audio attachment (if any) up so the form
    // can register it on the Source Recordings card.
    // v260.19 — also pass the per-field provenance map so AI-imported
    // fields can deep-link back to their source audio segments.
    onApply(picked, parsedResult.attachment || null, parsedResult.provenance || {});
    consumeJob(currentJobId);
    toast.success(`Applied ${Object.keys(picked).length} section(s) from transcript`);
    handleClose();
  };

  const pickedCount = Object.values(selectedSections).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(true); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="transcript-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-cyan-600" />
            Import Requirements from Transcript
          </DialogTitle>
          <DialogDescription>
            Upload a discovery / requirements transcript or record a voice memo.
            Audio runs through Whisper, then Claude Sonnet 4.5 extracts structured
            requirements that map onto each section. You pick which sections to merge.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-2">
          {/* ── Stage 1 — Upload + Record ── */}
          {stage === 'upload' && (
            <div className="space-y-3">
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`
                  border-2 border-dashed rounded-lg p-7 text-center cursor-pointer transition-colors
                  ${dragOver ? 'border-cyan-500 bg-cyan-50' : 'border-slate-300 hover:border-cyan-400 hover:bg-slate-50'}
                `}
                onClick={() => fileInputRef.current?.click()}
                data-testid="transcript-drop-zone"
              >
                <Upload className="w-10 h-10 mx-auto text-cyan-500 mb-2" />
                <div className="text-sm font-medium text-slate-700 mb-1">
                  Drop a transcript here, or click to choose
                </div>
                <div className="text-[11px] text-slate-500">
                  Text: {TEXT_EXTS.join(' • ')} (max 5 MB) &nbsp;·&nbsp; Audio: {AUDIO_EXTS.join(' • ')} (max 25 MB)
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ALLOWED_EXTS.join(',')}
                  className="hidden"
                  onChange={onFilePicked}
                  data-testid="transcript-file-input"
                />
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 text-[11px] text-slate-400 uppercase tracking-wider">
                <span className="flex-1 h-px bg-slate-200" />
                or record a voice memo
                <span className="flex-1 h-px bg-slate-200" />
              </div>

              {/* Voice memo recorder */}
              <div
                className={`
                  border rounded-lg p-4 flex items-center gap-3 transition-colors
                  ${recording ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'}
                `}
                data-testid="transcript-voice-recorder"
              >
                {recording ? (
                  <>
                    <div className="relative">
                      <AudioLines className="w-7 h-7 text-red-600" />
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-600 rounded-full animate-pulse" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-red-900 flex items-center gap-2">
                        Recording…
                        <span className="font-mono text-xs" data-testid="transcript-recording-timer">
                          {Math.floor(recordSeconds / 60).toString().padStart(2, '0')}:
                          {(recordSeconds % 60).toString().padStart(2, '0')}
                        </span>
                      </div>
                      <div className="text-[11px] text-red-700 mt-0.5">
                        Speak naturally — Whisper handles names, products, and budget figures.
                      </div>
                    </div>
                    <Button
                      onClick={() => stopRecording(false)}
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white"
                      data-testid="transcript-stop-record-btn"
                    >
                      <Square className="w-4 h-4 mr-1 fill-white" /> Stop &amp; transcribe
                    </Button>
                  </>
                ) : (
                  <>
                    <Mic className="w-7 h-7 text-cyan-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800">
                        Record a voice memo
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Talk through your call — we'll transcribe with Whisper and extract requirements with Claude.
                      </div>
                    </div>
                    <Button
                      onClick={startRecording}
                      size="sm"
                      variant="outline"
                      className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                      data-testid="transcript-start-record-btn"
                    >
                      <Mic className="w-4 h-4 mr-1" /> Start recording
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Stage 2 — Parsing ── */}
          {stage === 'parsing' && (
            <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="transcript-parsing-state">
              <Loader2 className="w-10 h-10 text-cyan-600 animate-spin mb-4" />
              <div className="text-sm font-medium text-slate-700">
                {asyncNote ? 'Analyzing a large transcript…' : 'Reading your transcript…'}
              </div>
              <div className="text-xs text-slate-500 mt-1 max-w-md">
                {asyncNote
                  ? 'This is a long transcript, so we\u2019re analyzing it in the background. It can take a minute or two — keep this window open and the results will appear here automatically when it\u2019s ready.'
                  : 'Audio files go through Whisper first, then Claude extracts requirements. 10-60 seconds.'}
              </div>
            </div>
          )}

          {/* ── Stage 3 — Preview ── */}
          {stage === 'preview' && parsedResult && (
            <div className="space-y-3" data-testid="transcript-preview">
              <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-center gap-2 text-xs text-emerald-800">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                <span>
                  Found data for <b>{parsedResult.sections_with_data?.length || 0}</b> section
                  {(parsedResult.sections_with_data?.length || 0) === 1 ? '' : 's'}{' '}
                  {parsedResult.transcribed_from_audio ? (
                    <>
                      from a <b>{Math.round((parsedResult.audio_bytes || 0) / 1024)} KB</b> audio recording
                      (Whisper transcript: <b>{parsedResult.transcript_chars?.toLocaleString() || '—'}</b> chars).
                    </>
                  ) : (
                    <>
                      from <b>{parsedResult.transcript_chars?.toLocaleString() || '—'}</b> characters of transcript.
                    </>
                  )}{' '}
                  Untick anything you don't want to apply.
                </span>
              </div>
              {(parsedResult.sections_with_data || []).length === 0 ? (
                <div className="text-center py-8 text-sm text-slate-500" data-testid="transcript-empty-result">
                  <AlertCircle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                  Claude couldn&apos;t extract any high-confidence requirements from this transcript.
                  Try a more detailed or focused conversation log.
                </div>
              ) : (
                (parsedResult.sections_with_data || []).map((secId) => {
                  const section = sectionsById[secId];
                  const sectionPayload = parsedResult.parsed[secId] || {};
                  const fieldsInSection = section?.fields || [];
                  return (
                    <div
                      key={secId}
                      className="border border-slate-200 rounded-lg p-3 bg-white"
                      data-testid={`transcript-section-${secId}`}
                    >
                      <div className="flex items-start gap-2">
                        <Checkbox
                          checked={!!selectedSections[secId]}
                          onCheckedChange={(v) => setSelectedSections((s) => ({ ...s, [secId]: !!v }))}
                          className="mt-1"
                          data-testid={`transcript-section-checkbox-${secId}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="font-semibold text-sm text-slate-800">
                              {section?.label || secId}
                            </div>
                            <Badge variant="outline" className="text-[10px] border-cyan-300 text-cyan-700 bg-cyan-50">
                              {Object.keys(sectionPayload).length} field{Object.keys(sectionPayload).length === 1 ? '' : 's'}
                            </Badge>
                          </div>
                          {section?.description && (
                            <div className="text-[11px] text-slate-500 mt-0.5">{section.description}</div>
                          )}
                          <div className="mt-2 space-y-1.5">
                            {Object.entries(sectionPayload).map(([fid, val]) => {
                              const fieldMeta = fieldsInSection.find((f) => f.id === fid);
                              return (
                                <div key={fid} className="text-xs">
                                  <span className="font-medium text-slate-700">
                                    {fieldMeta?.label || fid}:
                                  </span>{' '}
                                  <span className="text-slate-600 whitespace-pre-wrap break-words">
                                    {Array.isArray(val)
                                      ? (val.length === 0
                                        ? '—'
                                        : val.every((v) => typeof v === 'object')
                                          ? `${val.length} row${val.length === 1 ? '' : 's'}: ${val.map((r) => Object.values(r).filter(Boolean).join(' / ')).join(' • ')}`
                                          : val.join(', '))
                                      : typeof val === 'object' ? JSON.stringify(val) : String(val)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Error message */}
          {errorMsg && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-2 text-xs text-red-800" data-testid="transcript-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span className="flex-1">{errorMsg}</span>
              <button onClick={() => setErrorMsg('')} className="text-red-700 hover:text-red-900">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={handleClose} data-testid="transcript-cancel-btn">
            Cancel
          </Button>
          {stage === 'preview' && (
            <Button
              onClick={handleApply}
              disabled={pickedCount === 0}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="transcript-apply-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Apply {pickedCount} section{pickedCount === 1 ? '' : 's'} to form
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
